import type { CarbonSensitivityAttributeResult, CarbonSensitivityBlock } from '@siliconx/shared';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import { CalculationBasisTrigger } from '../CalculationBasisDrawer';
import { InfoTooltip } from '../InfoTooltip';
import { formatTonnes, kgToTonnes } from './carbonCategories';
import {
  CARBON_SENSITIVITY_CAPTION,
  CARBON_SENSITIVITY_EMPTY,
  CARBON_SENSITIVITY_METHOD_NOTE,
  CARBON_SENSITIVITY_NOT_APPLICABLE_PREFIX,
  CARBON_SENSITIVITY_TITLE,
} from './carbonCopy';
import { CarbonForecastFootnotes, CarbonMethodBadge, MethodologySheetTrigger } from './CarbonDisclosures';

/**
 * SIL-443 (W5, spec §11 "Ship-last (per charting): carbon sensitivity
 * tornado") — the carbon twin of the dollar tornado on this same route
 * (`SensitivityPanel` in routes/financials/scenarios/$scenarioId.tsx).
 *
 * ── WHY THIS EXTENDS THE TORNADO PATTERN RATHER THAN INVENTING A CHART ───
 * Same row grammar as the dollar tornado, deliberately: label on the left, a
 * centred bar whose two arms are the low/high bound, the signed swing on the
 * right, and an `applicable: false` row rendered GREYED WITH ITS REASON AND
 * NO BAR — a zero-length bar would read as "tested and doesn't matter", the
 * opposite of "couldn't test it". A reader who has learned the dollar
 * tornado reads this one with no new vocabulary.
 *
 * Two deliberate departures, both forced by the metric:
 *  1. The bar arms are coloured by DIRECTION OF FOOTPRINT (emerald = this
 *     bound lowers the footprint delta, violet = it raises it), not by the
 *     dollar tornado's red/green profit convention. Violet is the FORECAST
 *     card's own family; the sky/cyan family is never used here — it is
 *     reserved for the quarantined advisory module.
 *  2. The card carries the FULL forecast-card disclosure furniture (forecast
 *     badge, location-based method badge, methodology-sheet trigger,
 *     §1/§3/§5 footnotes) rather than the dollar tornado's bare white card.
 *     Any carbon number that can be screenshotted has to carry the framing
 *     line with it.
 *
 * FOOTPRINT DELTAS ONLY. Every figure rendered here comes from
 * `carbonSensitivity`, which the API computes from the footprint series'
 * `delta` alone (see `packages/shared/src/use-phase/carbon-sensitivity.ts`'s
 * header). This file imports nothing from the advisory module and must never
 * be given an advisory prop.
 */
export function CarbonSensitivityTornado({
  block,
  scenarioId,
}: {
  block: CarbonSensitivityBlock | null | undefined;
  /** Enables the methodology-sheet download; null for an unsaved draft. */
  scenarioId?: string | null;
}) {
  if (!block) return null;

  // Tornado ordering: widest swing at the top (that is what makes it a
  // tornado). Inapplicable rows sink to the bottom as a group rather than
  // sorting as if their swing were 0 — they were not measured at 0, they
  // were not measured.
  const rows = [...block.attributes].sort((a, b) => {
    if (a.applicable !== b.applicable) return a.applicable ? -1 : 1;
    return Math.abs(b.swingKgco2e ?? 0) - Math.abs(a.swingKgco2e ?? 0);
  });

  const maxArm = Math.max(
    ...rows.map((r) =>
      r.applicable && r.lowDeltaKgco2e !== null && r.highDeltaKgco2e !== null
        ? Math.max(
            Math.abs(r.lowDeltaKgco2e - r.baseDeltaKgco2e),
            Math.abs(r.highDeltaKgco2e - r.baseDeltaKgco2e),
          )
        : 0,
    ),
    0,
  );

  return (
    <div className="rounded-xl border-2 border-dashed border-violet-300 bg-violet-50/60 p-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center rounded bg-violet-600 px-1.5 py-0.5 text-xs font-bold uppercase tracking-wider text-white">
          Forecast
        </span>
        <SectionLabel className="inline-flex items-center gap-1.5 text-violet-900">
          {CARBON_SENSITIVITY_TITLE}
          <InfoTooltip label="About this sensitivity chart">
            {CARBON_SENSITIVITY_METHOD_NOTE}
          </InfoTooltip>
        </SectionLabel>
        <CarbonMethodBadge />
        {scenarioId && (
          <span className="ml-auto">
            <MethodologySheetTrigger scenarioId={scenarioId} />
          </span>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="mt-3 text-xs italic text-violet-800">{CARBON_SENSITIVITY_EMPTY}</p>
      ) : (
        <div className="mt-4 space-y-2.5">
          {rows.map((row) => (
            <CarbonSensitivityRow key={row.id} row={row} maxArm={maxArm} />
          ))}
        </div>
      )}

      <p className="mt-3 text-xs font-medium text-violet-800">{CARBON_SENSITIVITY_CAPTION}</p>

      <CarbonForecastFootnotes />
    </div>
  );
}

function CarbonSensitivityRow({
  row,
  maxArm,
}: {
  row: CarbonSensitivityAttributeResult;
  maxArm: number;
}) {
  const testable = row.applicable && row.lowDeltaKgco2e !== null && row.highDeltaKgco2e !== null;

  return (
    <div className="flex items-center gap-3">
      <div className="w-44 shrink-0">
        <span className="inline-flex items-center gap-1 text-xs font-medium text-zinc-700">
          {row.label}
          {row.applicable && <CalculationBasisTrigger title={row.label} basis={row.basis} />}
        </span>
        {testable && <div className="text-xs text-violet-700">{testedRangeLabel(row)}</div>}
      </div>

      {testable ? (
        <CarbonTornadoBar
          low={row.lowDeltaKgco2e!}
          high={row.highDeltaKgco2e!}
          base={row.baseDeltaKgco2e}
          maxArm={maxArm}
        />
      ) : (
        <div className="flex-1 text-xs italic text-zinc-400">
          {CARBON_SENSITIVITY_NOT_APPLICABLE_PREFIX} — {row.reason ?? 'no matching cohort in this scenario'}
        </div>
      )}

      <div className="w-28 shrink-0 text-right font-mono text-xs tabular-nums text-violet-800">
        {testable ? `±${formatTonnes(Math.abs(kgToTonnes(row.swingKgco2e ?? 0)))}` : ''}
      </div>
    </div>
  );
}

/** e.g. "−30% / +30%", or an asymmetric pair when a bound was clamped (PUE is
 * held inside its validated [1.0, 3.0] range, so a scenario already near a
 * limit tests a narrower band on that side — shown, never silently widened). */
function testedRangeLabel(row: CarbonSensitivityAttributeResult): string {
  const pct = (m: number | null): string => {
    if (m === null) return '—';
    const delta = Math.round((m - 1) * 1000) / 10;
    return `${delta > 0 ? '+' : delta < 0 ? '−' : '±'}${Math.abs(delta)}%`;
  };
  return `${pct(row.lowMultiplier)} / ${pct(row.highMultiplier)}`;
}

/**
 * The two arms, centred on the unperturbed footprint delta. Colour encodes
 * DIRECTION OF FOOTPRINT, not bound: whichever bound lowers the delta draws
 * emerald on the left, whichever raises it draws violet on the right — so a
 * driver whose "high" bound happens to reduce emissions still reads
 * correctly, instead of the reader having to remember which arm is which.
 */
function CarbonTornadoBar({
  low,
  high,
  base,
  maxArm,
}: {
  low: number;
  high: number;
  base: number;
  maxArm: number;
}) {
  // maxArm === 0 means every applicable driver swung exactly 0 — a true,
  // disclosed zero (the bar renders as the bare centre line, and the ±0
  // figure beside it says so). Guard the divide rather than inventing scale.
  const scale = maxArm > 0 ? maxArm : 1;
  const lowDelta = low - base;
  const highDelta = high - base;
  const downPct = (Math.max(0, -Math.min(lowDelta, highDelta)) / scale) * 50;
  const upPct = (Math.max(0, Math.max(lowDelta, highDelta)) / scale) * 50;

  return (
    <div className="relative h-4 flex-1 rounded bg-white/70 ring-1 ring-inset ring-violet-100">
      <div className="absolute inset-y-0 left-1/2 w-px bg-violet-300" />
      <div
        className="absolute inset-y-0 rounded-l bg-emerald-300"
        style={{ right: '50%', width: `${downPct}%` }}
      />
      <div
        className="absolute inset-y-0 rounded-r bg-violet-400"
        style={{ left: '50%', width: `${upPct}%` }}
      />
    </div>
  );
}
